// useContractsMap — TanStack Query wrapper around POST /v1/map/contracts.
//
// Powers the Intel map's `contracts-hex` overlay layer. Default-OFF in the
// legend, so we gate the fetch on `enabled` to avoid burning a request
// every time the page mounts.
//
// Filter-aware: the same `FilterState` the intel filter rail emits is
// posted in the body. The server compiles only fields it knows about
// (intersection of awards + contracts schemas — jurisdiction, agency,
// vendor, procurement category/method, dates, status, title), so the
// hexes narrow correctly when the user selects e.g. a procurement
// category or jurisdiction. Intel-only fields (signal_type, severity,
// NAICS) are silently ignored server-side because awards/contracts don't
// carry them.
//
// The endpoint today returns province-centroid aggregates; the H3 binner
// in `@eidollon/intel-feed-client/layers/contracts-hex` treats each row's
// `count` as the weight, so density renders correctly even at this coarse
// shape. When the real per-contract geocoding lands the same code path
// produces street-level hex subdivision automatically.

import { API_BASE_URL } from '@/lib/env';
import { readAccessToken } from '@/state/auth';
import type { filters as f } from '@eidollon/contracts';
import type { ContractMapPoint } from '@eidollon/intel-feed-client';
import { useQuery } from '@tanstack/react-query';

interface ContractsMapResponse {
  items: ContractMapPoint[];
}

export interface UseContractsMapResult {
  contracts: readonly ContractMapPoint[];
  isLoading: boolean;
  error: string | null;
}

const EMPTY: readonly ContractMapPoint[] = [];

export function useContractsMap(
  enabled: boolean,
  filterState: f.FilterState,
): UseContractsMapResult {
  const token = readAccessToken() ?? '';

  const query = useQuery({
    queryKey: ['map-contracts', filterState, token],
    enabled,
    queryFn: async (): Promise<ContractsMapResponse> => {
      const res = await fetch(`${API_BASE_URL}/v1/map/contracts`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ state: filterState }),
      });
      if (!res.ok) throw new Error(`map/contracts ${res.status}`);
      const json = (await res.json()) as ContractsMapResponse;
      return { items: Array.isArray(json?.items) ? json.items : [] };
    },
    // 60s so toggling a facet on/off/on within the window is a cache hit.
    staleTime: 60_000,
  });

  return {
    contracts: query.data?.items ?? EMPTY,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
  };
}
